import { createContext, useContext, useState } from 'react';
import {
  createFileState,
  downloadFile,
  exportFileState,
  importFileState,
} from 'util/file';
import {
  archiveStorageUnitRequest,
  createStorageUnitRequest,
  deleteAllArchivedStorageUnitRequest,
  removeStorageUnitRequest,
  restoreStorageUnitRequest,
  updateStorageUnitRequest,
} from 'store/storageunits/sagaActions';
import { useDispatch } from 'react-redux';
import type {
  ProjectMetadata,
  WorkflowMetadata,
  StorageUnitStatus,
} from 'store/storageunits/types';
import {
  FileUploadStatus,
  StorageUnitType,
  UpdateActionOrigin,
} from 'store/storageunits/types';
import { getOne } from 'api/cloudsave/cloudsaveApi';
import { splitName } from 'util/nameHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from 'util/routes';
import { addUpload } from 'store/storageunits/reducer';
import { readFile } from 'util/readFile';
import { addMessage } from 'store/snackbar/reducer';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import { downloadAndCache } from 'util/downloadAndCache';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';

export enum ModalType {
  CREATE_FILE = 'CREATE_FILE',
  CREATE_FOLDER = 'CREATE_FOLDER',
  CREATE_PROJECT = 'CREATE_PROJECT',
  EDIT_PROJECT = 'EDIT_PROJECT',
  EDIT_FILE = 'EDIT_FILE',
  RENAME_FILE = 'RENAME_FILE',
  RENAME_FOLDER = 'RENAME_FOLDER',
  DELETE_UNIT = 'DELETE_UNIT',
  DELETE_SHARED_UNIT = 'DELETE_SHARED_UNIT',
  MOVE_UNIT = 'MOVE_UNIT',
  RENAME_TEMPLATE = 'RENAME_TEMPLATE',
  SHARE = 'SHARE',
  USE_TEMPLATE = 'USE_TEMPLATE',
  SAVE_AS = 'SAVE_AS',
  SAVE_A_COPY = 'SAVE_A_COPY',
  CREATE_TEMPLATE = 'CREATE_TEMPLATE',
  DELETE_ARCHIVED = 'DELETE_ARCHIVED',
}

export type Modal = {
  id?: string;
  name?: string;
  type: ModalType;
  storageUnitType: StorageUnitType;
  useConflictDraft?: boolean;
};

type CreateFileOptions = {
  isUpload?: boolean;
  openAfterCreate?: boolean;
  fileStatus?: StorageUnitStatus[];
  flowsheetMetadata?: WorkflowMetadata;
  undoable?: boolean;
};

export interface Context {
  modal: Modal | null;
  downloadingFiles: Set<string>;
  createFile: (
    name: string,
    parentId?: string,
    fileState?: string,
    options?: CreateFileOptions,
  ) => void;
  createFolder: (name: string, parentId?: string) => void;
  createProject: (
    name: string,
    parentId?: string,
    metadata?: ProjectMetadata,
  ) => void;
  updateProject: (id: string, name: string, metadata: ProjectMetadata) => void;
  renameUnit: (
    id: string,
    newName: string,
    type: StorageUnitType,
    origin?: UpdateActionOrigin,
  ) => void;
  moveUnit: (
    id: string,
    name: string,
    newParentId: string,
    storageUnitType: StorageUnitType,
    origin?: UpdateActionOrigin,
  ) => void;
  deleteUnit: (id: string) => void;
  archiveUnit: (id: string) => void;
  restoreUnit: (id: string) => void;
  deleteArchived: () => void;
  importFile: (file: File, parentId?: string) => void;
  exportFile: (id: string, name: string) => Promise<void>;
  duplicateFile: (id: string, name: string, parentId?: string) => Promise<void>;
  downloadForOffline: (id: string) => Promise<void>;
  openModal: (modal: Modal) => void;
  closeModal: () => void;
  openFile: (id: string) => void;
  openFolder: (id: string) => void;
}

const StorageUnitContext = createContext<Context | null>(null);

export const useStorageUnits = () => {
  const context = useContext(StorageUnitContext);

  if (!context) {
    throw new Error('Cannot use hook outside of context');
  }

  return context;
};

type Props = {
  children: React.ReactNode;
};

export const StorageUnitProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const { pathname, search } = useLocation();
  const [modal, setModalOpen] = useState<Modal | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );

  const createFile = (
    name: string,
    parentId?: string,
    fileState?: string,
    options?: CreateFileOptions,
  ) => {
    try {
      const newState = fileState
        ? importFileState(name, fileState, true)
        : createFileState(name, options?.flowsheetMetadata);
      const newFile = exportFileState(newState);
      dispatch(
        createStorageUnitRequest(name, parentId, newFile, options?.fileStatus, {
          isUpload: options?.isUpload,
          openAfterCreate: options?.openAfterCreate,
          navigate,
          flowsheetMetadata: options?.flowsheetMetadata,
          undoable: options?.undoable,
        }),
      );
    } catch (err) {
      dispatch(addMessage(errorMessage('Corrupt file')));
      dispatch(
        addUpload({
          id: new Date().getTime().toString(),
          name,
          status: FileUploadStatus.FAILED,
          errorText: 'Corrupt file',
        }),
      );
    }
  };

  const createFolder = (name: string, parentId?: string) => {
    dispatch(createStorageUnitRequest(name, parentId));
  };

  const createProject = (
    name: string,
    parentId?: string,
    metadata?: ProjectMetadata,
  ) => {
    dispatch(
      createStorageUnitRequest(name, parentId, undefined, undefined, {
        projectMetadata: metadata,
      }),
    );
  };

  const updateProject = (
    id: string,
    name: string,
    metadata: ProjectMetadata,
  ) => {
    dispatch(
      updateStorageUnitRequest(
        {
          id,
          name,
          type: StorageUnitType.Folder,
          projectMetadata: metadata,
        },
        UpdateActionOrigin.RENAME_MODAL,
      ),
    );
  };

  const renameUnit = (
    id: string,
    newName: string,
    type: StorageUnitType,
    origin: UpdateActionOrigin = UpdateActionOrigin.RENAME_MODAL,
  ) => {
    dispatch(updateStorageUnitRequest({ id, name: newName, type }, origin));
  };

  const deleteUnit = (id: string) => {
    dispatch(removeStorageUnitRequest(id));
  };

  const archiveUnit = (id: string) => {
    dispatch(archiveStorageUnitRequest(id));
  };

  const restoreUnit = (id: string) => {
    dispatch(restoreStorageUnitRequest(id));
  };

  const deleteArchived = () => {
    dispatch(deleteAllArchivedStorageUnitRequest());
  };

  const moveUnit = (
    id: string,
    name: string,
    newParentId: string,
    storageUnitType: StorageUnitType,
    origin: UpdateActionOrigin = UpdateActionOrigin.MOVE_MODAL,
  ) => {
    const body = {
      id: id,
      name: name,
      parentId: newParentId,
      type: storageUnitType,
    };
    dispatch(updateStorageUnitRequest(body, origin));
  };

  const importFile = (file: File, parentId?: string) => {
    try {
      readFile(file, (fileContent) => {
        createFile(file.name, parentId, fileContent, { isUpload: true });
      });
    } catch (error) {
      dispatch(addMessage(errorMessage("Couldn't import file")));
      dispatch(
        addUpload({
          id: new Date().getTime().toString(),
          name: file.name,
          status: FileUploadStatus.FAILED,
          errorText: 'Incorrect file format',
        }),
      );
    }
  };

  const exportFile = async (id: string, name: string) => {
    let cachedFileData: string | undefined;
    try {
      const cached = await storageUnitCache.getOne(id);
      cachedFileData = cached?.fileData;
    } catch (_err) {
      cachedFileData = undefined;
    }

    const downloadNgpd = (fileData?: string) => {
      if (!fileData) {
        return false;
      }
      downloadFile(splitName(StorageUnitType.File, name), undefined, fileData);
      return true;
    };

    if (!isOnline) {
      if (!downloadNgpd(cachedFileData)) {
        dispatch(addMessage(errorMessage("Couldn't export file")));
      }
      return;
    }

    try {
      const { parsedBody } = await getOne(id);
      if (!downloadNgpd(parsedBody?.fileData ?? cachedFileData)) {
        dispatch(addMessage(errorMessage("Couldn't export file")));
      }
    } catch (err) {
      if (!downloadNgpd(cachedFileData)) {
        dispatch(addMessage(errorMessage("Couldn't export file")));
      }
    }
  };

  const duplicateFile = async (id: string, name: string, parentId?: string) => {
    const { parsedBody } = await getOne(id);
    if (parsedBody?.fileData) {
      const newName = `${splitName(StorageUnitType.File, name)} - Copy`;
      createFile(newName, parentId, parsedBody.fileData, {
        undoable: false,
      });
    }
  };

  const downloadForOffline = async (id: string) => {
    setDownloadingFiles((prev) => new Set(prev).add(id));

    try {
      await downloadAndCache(id);
    } catch (err) {
      dispatch(
        addMessage(errorMessage("Couldn't download file for offline use")),
      );
      throw err;
    } finally {
      setDownloadingFiles((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const openModal = (m: Modal) => {
    setModalOpen(m);
  };

  const closeModal = () => {
    setModalOpen(null);
  };

  const openFile = (id: string) => {
    navigate(routes.file(id) + search, { state: { lastLocation: pathname } });
  };

  const openFolder = (id: string) => {
    navigate(routes.folder(id) + search);
  };

  return (
    <StorageUnitContext.Provider
      value={{
        modal,
        downloadingFiles,
        createFile,
        createFolder,
        createProject,
        updateProject,
        renameUnit,
        archiveUnit,
        restoreUnit,
        deleteArchived,
        moveUnit,
        importFile,
        exportFile,
        duplicateFile,
        downloadForOffline,
        openModal,
        closeModal,
        deleteUnit,
        openFile,
        openFolder,
      }}
    >
      {children}
    </StorageUnitContext.Provider>
  );
};
